import { gaPushEvent } from "../base.analytics";

/**
 * The tracking of the JuisciGPT wizard (docs/juiscigpt-wizard.md REQ-25 to
 * REQ-30, PI-6 section 6). The analysis computes the skips, the exits and the
 * time on each step from these events: an exit is a step that the reader saw,
 * without the next step and without an end. Each event carries the gate and
 * the level of the tour question (REQ-28).
 */
enum Event {
  ASSIGNED = "juiscigpt_wizard_assigned",
  STEP_SHOWN = "juiscigpt_wizard_step_shown",
  CHOICE = "juiscigpt_wizard_choice",
  ENDED = "juiscigpt_wizard_ended",
}

// The entry, each screen of note 1, the other notes, the trial and the pause.
export type WizardTrackedStep =
  | "invitation"
  | "room_sheet"
  | "question_card"
  | "note_1_search"
  | "note_1_sort"
  | "note_1_write"
  | "note_2"
  | "note_3"
  | "trial"
  | "pause";

// Null when the question of the tour left the catalogue.
export type WizardTrackedContext = {
  gate: "first_visit" | "room";
  questionLevel: "specialty" | "general" | null;
};

/**
 * The draw of the reader (REQ-29): `control` never sees the wizard. Once per
 * session, on the JuisciGPT home, for every reader the flag reaches.
 */
export function gaEventJuisciGptWizardAssigned(params: {
  group: "tour" | "control";
}) {
  gaPushEvent(Event.ASSIGNED, { wizard_group: params.group });
}

export function gaEventJuisciGptWizardStepShown(
  params: WizardTrackedContext & { step: WizardTrackedStep }
) {
  gaPushEvent(Event.STEP_SHOWN, {
    step: params.step,
    gate: params.gate,
    question_level: params.questionLevel,
  });
}

/**
 * The reader's answer to an entry: the skip rate, and for the room sheet the
 * guardrail of REQ-30 (a close in less than 3 s without the tour).
 */
export function gaEventJuisciGptWizardChoice(
  params: WizardTrackedContext & {
    entry: "invitation" | "room_sheet" | "question_card";
    choice: "start" | "later" | "adapt";
    secondsSinceShown: number | null;
  }
) {
  gaPushEvent(Event.CHOICE, {
    entry: params.entry,
    choice: params.choice,
    seconds_since_shown: params.secondsSinceShown,
    gate: params.gate,
    question_level: params.questionLevel,
  });
}

/**
 * The end of a tour: `completed` at the second question, `skipped` from the
 * pause banner, `error` for the forced exit of an engine error (REQ-27).
 */
export function gaEventJuisciGptWizardEnded(
  params: WizardTrackedContext & {
    reason: "completed" | "skipped" | "error";
    step: WizardTrackedStep;
  }
) {
  gaPushEvent(Event.ENDED, {
    reason: params.reason,
    step: params.step,
    gate: params.gate,
    question_level: params.questionLevel,
  });
}
